import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { OrgService } from '@/services/OrgService';
import { AccountService } from '@/services/AccountService';
import { OrgKnowledgeService } from '@/services/OrgKnowledgeService';

// Query Keys
export const queryKeys = {
  session: ['session'] as const,
  orgs: ['orgs'] as const,
  orgMembers: (orgId: string) => ['org', orgId, 'members'] as const,
  orgInvites: (orgId: string) => ['org', orgId, 'invites'] as const,
  orgBackgrounds: (orgId: string) => ['org', orgId, 'backgrounds'] as const,
  orgDashboardStats: (orgId: string) => ['org', orgId, 'dashboard', 'stats'] as const,
  orgDashboardActivity: (orgId: string) => ['org', orgId, 'dashboard', 'activity'] as const,
  drafts: (filters: Record<string, any>) => ['drafts', filters] as const,
  adminJobs: (filters: Record<string, any>) => ['admin', 'jobs', filters] as const,
  adminJobsStats: (window: string) => ['admin', 'jobs', 'stats', window] as const,
  adminJob: (jobId: string) => ['admin', 'job', jobId] as const,
  adminJobEvents: (jobId: string) => ['admin', 'job', jobId, 'events'] as const,
} as const;

// Org Members Query
export function useOrgMembers(orgId?: string) {
  const svc = new OrgService();
  return useQuery({
    queryKey: queryKeys.orgMembers(orgId || ''),
    queryFn: () => svc.listMembers(orgId!),
    enabled: !!orgId,
    staleTime: 2 * 60_000, // 2 minutes
    gcTime: 5 * 60_000,
  });
}

// Org Invites Query
export function useOrgInvites(orgId?: string) {
  const svc = new OrgService();
  return useQuery({
    queryKey: queryKeys.orgInvites(orgId || ''),
    queryFn: () => svc.listInvites(orgId!),
    enabled: !!orgId,
    staleTime: 2 * 60_000,
    gcTime: 5 * 60_000,
  });
}

// Org Backgrounds Query
// useOrgBackgrounds removed in Milestone 3

// Admin Jobs Query
export function useAdminJobs(filters: Record<string, any> = {}) {
  return useQuery({
    queryKey: queryKeys.adminJobs(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, String(value));
      });
      const res = await apiFetch(`/api/admin/jobs?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const json = await res.json();
      return json.success ? { items: json.data, pagination: json.pagination } : { items: [], pagination: null };
    },
    staleTime: 30_000, // 30 seconds for admin data
    gcTime: 2 * 60_000,
  });
}

// Admin Jobs Stats Query
export function useAdminJobsStats(window: string = '24h') {
  return useQuery({
    queryKey: queryKeys.adminJobsStats(window),
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/jobs/stats?window=${window}`);
      if (!res.ok) throw new Error('Failed to fetch stats');
      const json = await res.json();
      return json.success ? json.data : null;
    },
    staleTime: 60_000, // 1 minute for stats
    gcTime: 5 * 60_000,
  });
}

// Admin Job Details Query
export function useAdminJob(jobId?: string) {
  return useQuery({
    queryKey: queryKeys.adminJob(jobId || ''),
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/jobs/${jobId}`);
      if (!res.ok) throw new Error('Failed to fetch job');
      const json = await res.json();
      return json.success ? json.data : null;
    },
    enabled: !!jobId,
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  });
}

// Admin Job Events Query
export function useAdminJobEvents(jobId?: string) {
  return useQuery({
    queryKey: queryKeys.adminJobEvents(jobId || ''),
    queryFn: async () => {
      const res = await apiFetch(`/api/admin/jobs/${jobId}/events`);
      if (!res.ok) throw new Error('Failed to fetch events');
      const json = await res.json();
      return json.success ? json.data : [];
    },
    enabled: !!jobId,
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  });
}

// Mutations for Members & Invites
export function useInviteMemberMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();
  
  return useMutation({
    mutationFn: ({ orgId, email, roleIds }: { orgId: string; email: string; roleIds: string[] }) =>
      svc.invite(orgId, email, roleIds),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgInvites(orgId) });
    },
  });
}

export function useUpdateMemberRoleMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();
  
  return useMutation({
    mutationFn: ({ orgId, userId, role }: { orgId: string; userId: string; role: 'admin' | 'member' | 'owner' }) =>
      svc.updateMemberRole(orgId, userId, role),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers(orgId) });
    },
  });
}

export function useRemoveMemberMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();
  
  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      svc.removeMember(orgId, userId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers(orgId) });
    },
  });
}

export function useRevokeInviteMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgService();
  
  return useMutation({
    mutationFn: ({ orgId, inviteId }: { orgId: string; inviteId: string }) =>
      svc.revokeInvite(orgId, inviteId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgInvites(orgId) });
    },
  });
}

// Hook to get active org ID from store
export function useActiveOrgId() {
  return useOrgStore(s => s.activeOrgId);
}

// Account/Profile queries
export function useAccountProfile() {
  const svc = new AccountService();
  return useQuery({
    queryKey: ['account', 'profile'] as const,
    queryFn: () => svc.getProfile(),
    staleTime: 5 * 60_000, // 5 minutes
    gcTime: 10 * 60_000,
  });
}

export function useAccountSessions() {
  const svc = new AccountService();
  return useQuery({
    queryKey: ['account', 'sessions'] as const,
    queryFn: () => svc.listSessions(),
    staleTime: 2 * 60_000, // 2 minutes
    gcTime: 5 * 60_000,
  });
}

// Organization Knowledge Profile query
export function useOrgKnowledgeProfile() {
  const svc = new OrgKnowledgeService();
  return useQuery({
    queryKey: ['orgKnowledge', 'profile'] as const,
    queryFn: () => svc.getProfile(),
    staleTime: 5 * 60_000, // 5 minutes
    gcTime: 10 * 60_000,
  });
}

// Account mutations
export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  const svc = new AccountService();
  
  return useMutation({
    mutationFn: (data: { fullName: string }) => svc.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'profile'] });
    },
  });
}

export function useRevokeSessionMutation() {
  const queryClient = useQueryClient();
  const svc = new AccountService();
  
  return useMutation({
    mutationFn: (sessionId: string) => svc.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] });
    },
  });
}

// Organization Knowledge mutations
export function useSaveOrgKnowledgeProfileMutation() {
  const queryClient = useQueryClient();
  const svc = new OrgKnowledgeService();
  
  return useMutation({
    mutationFn: (data: any) => svc.saveProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgKnowledge', 'profile'] });
    },
  });
}
